import { isDefined, isNonEmpty } from '@sgme/fp';
import type { EnrichmentError } from 'state/common-form/infos/infos.model';
import { getFormMetadataByFormId } from 'state/forms/metadata/selectors';
import { getRejections } from 'state/forms/rejections/selectors';
import { getBookingType } from 'state/quotes/infos/infos.selectors';
import { getQuoteInfos } from 'state/quotes/pendings/pendings.selectors';
import { getQuoteResult } from 'state/quotes/results/results.selectors';
import { hasStreamingPriceByTabId } from 'state/quotes/streaming/streaming.selector';
import type { PriceEnhancementMode, TradeQuoteAction } from 'state/quotes/trades/trades.model';
import { isLiveQuote, isPastQuote } from 'state/quotes/utils';
import { getFormQuoteType, getFormRequestType } from 'state/selectors';
import {
  getDynamicFormInfos,
  getDynamicQuoteRequestStartingTimeInMillis,
  getDynamicQuoteResult,
  getDynamicQuoteState,
  getDynamicTechnicalError,
} from 'state/selectors/common/dynamicForm';
import { getTradeInfosByWorkspaceTab } from 'state/selectors/common/temp';
import {
  mapTradeAuthorizationStatusToWorkflowStatus,
  mapTradeStatusToWorkflowStatus,
} from 'state/selectors/meta/quote/utils';
import type { AppState } from 'state/state.model';
import type { WorkFlowStatus } from 'state/static/staticForm.selectors';

export function getDynamicFormEnrichmentErrorCorrelationId(state: AppState, tabId: string): string | undefined {
  const infos = getDynamicFormInfos(state, tabId);
  if (infos?.enrichmentError === undefined) {
    return undefined;
  }
  const enrichmentError = infos.enrichmentError as EnrichmentError;
  return enrichmentError?.correlationId;
}

export function isDynamicFormLoaded(state: AppState, tabId: string): boolean {
  if (!getFormMetadataByFormId(state, 'dynamic', tabId)) {
    return false;
  }
  const infos = getDynamicFormInfos(state, tabId);
  if (!infos) {
    return false;
  }
  return true;
}

export function getDynamicWorkflowStatus(state: AppState, tabId: string): WorkFlowStatus {
  const formInfos = getDynamicFormInfos(state, tabId);
  const quoteRequestTimeInMillis = getDynamicQuoteRequestStartingTimeInMillis(state, tabId);
  const quoteState = getDynamicQuoteState(state, tabId);
  const quoteId = formInfos?.quoteId;
  const bookingType = isNonEmpty(quoteId) ? getBookingType(state, quoteId) : undefined;
  const requestType = getFormRequestType(state, 'dynamic', tabId);
  const technicalError = getDynamicTechnicalError(state, tabId, false);
  const tradeInfos = getTradeInfosByWorkspaceTab(state, 'dynamic', tabId);
  const rejections = getRejections(state, tabId);
  const quoteType = getFormQuoteType(state, 'dynamic', tabId);
  if (requestType === 'RequestForStream') {
    if (rejections.length > 0 || tradeInfos.tradeStatus === 'Rejected') {
      return 'REJECTED';
    }
    if (technicalError !== undefined) {
      return 'ERROR';
    }

    if (tradeInfos?.tradeStatus !== undefined && isLiveQuote(quoteState)) {
      const status = mapTradeStatusToWorkflowStatus(tradeInfos.tradeStatus);
      if (status === 'TRADED') {
        return 'STREAMING_TRADED';
      }
    }
    if (isPastQuote(quoteState)) {
      return 'STREAM_EXPIRED';
    }

    if (quoteRequestTimeInMillis) {
      if (!hasStreamingPriceByTabId(state, tabId)) {
        return 'STREAMING_INITIATED';
      }
      if (quoteType === 'Tradable') {
        return 'STREAMING_TRADABLE';
      }
      return 'STREAMING';
    }
  }

  if (technicalError !== undefined) {
    return 'ERROR';
  }

  if (isDefined(bookingType) && isNonEmpty(quoteId)) {
    return getDynamicWorkflowForBookingStatus(state, tabId, quoteId);
  }

  if (!formInfos) {
    return 'UNDEFINED';
  }

  if (formInfos.version === 0) {
    return 'INITIALIZED';
  }

  if (quoteRequestTimeInMillis) {
    return 'QUOTING';
  }

  if (tradeInfos?.tradeStatus !== undefined && isLiveQuote(quoteState)) {
    return mapTradeStatusToWorkflowStatus(tradeInfos.tradeStatus);
  }

  const quoteResult = getDynamicQuoteResult(state, tabId);
  if (quoteResult) {
    if (quoteResult.status === 'Quoted') {
      if (isPastQuote(quoteState)) {
        return 'QUOTED_PREVIOUSLY';
      }
      return mapTradeAuthorizationStatusToWorkflowStatus(tradeInfos?.tradeAuthStatus);
    } else if (quoteResult.status === 'Rejected' || quoteResult.status === 'Failed') {
      return 'REJECTED';
    }
  }
  if (formInfos.actions?.some(x => !x.isDisabled)) {
    return 'READY';
  }

  return 'INITIALIZED';
}

export function getDynamicWorkflowForBookingStatus(state: AppState, tabId: string, quoteId: string): WorkFlowStatus {
  const formInfos = getDynamicFormInfos(state, tabId);
  const quoteState = getDynamicQuoteState(state, tabId);

  const technicalError =
    getQuoteResult(state.quotes.results, quoteId)?.technicalError ??
    getQuoteInfos(state.quotes.pendings, tabId)?.technicalError;

  const tradeInfos = getTradeInfosByWorkspaceTab(state, 'dynamic', tabId);

  if (!formInfos) {
    return 'UNDEFINED';
  }

  if (formInfos.version === 0) {
    return 'INITIALIZED';
  }
  if (technicalError !== undefined) {
    return 'ERROR';
  }

  if (tradeInfos?.tradeStatus !== undefined && isLiveQuote(quoteState)) {
    return mapTradeStatusToWorkflowStatus(tradeInfos.tradeStatus, true);
  }

  const tradeAuthStatus = tradeInfos?.tradeAuthStatus;

  if (isDefined(tradeAuthStatus) && !['Granted', 'Pending'].includes(tradeAuthStatus)) {
    return mapTradeAuthorizationStatusToWorkflowStatus(tradeInfos?.tradeAuthStatus);
  }

  const quoteResult = getDynamicQuoteResult(state, tabId);
  if (quoteResult) {
    if (quoteResult.status === 'Quoted') {
      if (isPastQuote(quoteState)) {
        return 'QUOTED_PREVIOUSLY';
      }
      return mapTradeAuthorizationStatusToWorkflowStatus(tradeInfos?.tradeAuthStatus);
    } else if (quoteResult.status === 'Rejected' || quoteResult.status === 'Failed') {
      return 'REJECTED';
    }
  }
  if (formInfos.actions?.some(x => !x.isDisabled)) {
    return 'READY';
  }

  return 'INITIALIZED';
}

export function getDynamicFormPriceEnhancementMode(state: AppState, tabId: string): PriceEnhancementMode {
  const infos = getDynamicFormInfos(state, tabId);
  if (!infos?.actions) {
    return 'None';
  }
  const tradeAction = infos.actions.find(x => x.name === 'TradableQuote' || x.name === 'ExecOnly') as TradeQuoteAction;
  if (!tradeAction) {
    return 'None';
  }
  return tradeAction.priceEnhancement;
}
